import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiUsers,
  FiSettings,
  FiBell,
  FiShield,
  FiUserPlus,
  FiLink,
  FiBookOpen
} = FiIcons

const AdminPanel = () => {
  const [users, setUsers] = useState([
    { id: 1, name: 'Jordan Smith', role: 'teacher' },
    { id: 2, name: 'Ava Martinez', role: 'parent' },
    { id: 3, name: 'Noah Brown', role: 'student' }
  ])
  const [announcement, setAnnouncement] = useState('Quarter 2 scope & sequence available for review.')
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    librarySharing: true
  })

  const updateRole = (id, role) => {
    setUsers((prev) => prev.map((user) => (user.id === id ? { ...user, role } : user)))
  }

  const toggleSetting = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#1B4C87] to-[#00AAFF] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-3">Admin Panel</h1>
        <p className="text-blue-50 max-w-3xl">
          Oversee users, assign teachers to students, manage the curriculum library, and broadcast
          platform-wide announcements.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Management */}
        <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiUsers} className="w-5 h-5 text-[#00AAFF]" />
            <h2 className="text-xl font-bold text-gray-800">User Management</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {users.map((user) => (
              <div key={user.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                <p className="font-semibold text-gray-800">{user.name}</p>
                <select
                  className="mt-2 w-full border border-gray-200 rounded-lg px-2 py-1 text-sm"
                  value={user.role}
                  onChange={(e) => updateRole(user.id, e.target.value)}
                >
                  <option value="student">Student</option>
                  <option value="parent">Parent</option>
                  <option value="teacher">Teacher</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiSettings} className="w-5 h-5 text-[#1B4C87]" />
            <h3 className="text-lg font-bold text-gray-800">Platform Settings</h3>
          </div>
          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <p className="font-semibold text-gray-800">Maintenance mode</p>
              <p className="text-sm text-gray-600">Temporarily hide dashboards from users.</p>
            </div>
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={() => toggleSetting('maintenanceMode')}
            />
          </label>
          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <p className="font-semibold text-gray-800">Share curriculum library</p>
              <p className="text-sm text-gray-600">Allow teachers to publish to the shared catalog.</p>
            </div>
            <input
              type="checkbox"
              checked={settings.librarySharing}
              onChange={() => toggleSetting('librarySharing')}
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assign Teachers */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3 lg:col-span-2">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiLink} className="w-5 h-5 text-[#00AAFF]" />
            <h3 className="text-lg font-bold text-gray-800">Assign Teachers to Students</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="font-semibold text-gray-800">Student: Mia Green</p>
              <p className="text-sm text-gray-600">Assigned Teacher: Jordan Smith</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="font-semibold text-gray-800">Student: Kai Patel</p>
              <p className="text-sm text-gray-600">Assigned Teacher: Ava Martinez</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Pairings can be updated as families request new mentors or as class sizes change.
          </p>
        </div>

        {/* System Library */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-[#1B4C87]" />
            <h3 className="text-lg font-bold text-gray-800">System Curriculum Library</h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="p-2 bg-gray-50 rounded-lg border border-gray-200">Nature Studies - K-5</li>
            <li className="p-2 bg-gray-50 rounded-lg border border-gray-200">Foundations of Writing</li>
            <li className="p-2 bg-gray-50 rounded-lg border border-gray-200">STEM Explorers Lab</li>
          </ul>
        </div>
      </div>

      {/* Announcements */}
      <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiBell} className="w-5 h-5 text-[#F6A800]" />
          <h3 className="text-lg font-bold text-gray-800">Platform Announcements</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <p className="font-semibold text-gray-800">Featured message</p>
            <p className="text-sm text-gray-700">{announcement}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <p className="font-semibold text-gray-800">Support</p>
            <p className="text-sm text-gray-700">support@prosperityacademy.com</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <p className="font-semibold text-gray-800 flex items-center gap-1">
              <SafeIcon icon={FiShield} className="w-4 h-4 text-[#10B981]" />
              Compliance
            </p>
            <p className="text-sm text-gray-700">FERPA & COPPA-aligned privacy guardrails</p>
          </div>
        </div>
        <textarea
          className="w-full border border-gray-200 rounded-lg px-3 py-2"
          rows="3"
          value={announcement}
          onChange={(e) => setAnnouncement(e.target.value)}
        />
        <div className="flex justify-end">
          <button className="px-4 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0095E0]">
            Publish Announcement
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminPanel
